import { Injectable } from '@angular/core';
import {
  Action,
  Selector,
  State,
  StateContext,
  StateToken,
  Store,
  createSelector,
} from '@ngxs/store';
import { insertItem, patch, updateItem } from '@ngxs/store/operators';
import { map } from 'rxjs';
import { tap } from 'rxjs/operators';
import { DueListRoutes, UserTasksRoutes } from 'src/app/core/enums/routes.enum';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { MenuTreeStructureState } from 'src/app/core/state/menu-tree-structure.state';
import { ClearLastDueListItem } from 'src/app/due-list/state/due-list.actions';
import { DueListState } from 'src/app/due-list/state/due-list.state';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { redirectAndBypassUnsavedDataCheck } from 'src/app/shared/functions/redirect-and-bypass-unsaved-data-check';
import { IdName } from 'src/app/shared/models/id-name';
import { TaskLog } from '../models/task-log.model';
import {
  IApiResponseGroupTasks,
  ITask,
  ITaskListItem,
  ITaskPerformResponse,
  ITasksGroupData,
} from '../models/task.model';
import { TaskApiService } from '../services/task-api.service';
import {
  ClearCurrentTask,
  ClearTasksByMenu,
  CompleteTask,
  CreateTask,
  DeactivateTask,
  DeleteTask,
  LoadTaskById,
  LoadTaskDraftPerform,
  LoadTaskLog,
  LoadTaskVersion,
  LoadTasks,
  LoadTasksForIO,
  PerformTask,
  PostponeTask,
  ReactivateTask,
  RestoreTask,
  SaveTaskDraftPerform,
  UpdateTask,
} from './task.actions';

export interface TasksStateModel {
  tasks: ITask[];
  tasksOverview: ITasksGroupData[];
  tasksOverviewTotalPages: number;
  taskList: ITaskListItem[];
  taskLog: TaskLog;
  taskVersions: ITaskPerformResponse[];
  draftPerform: ITaskPerformResponse | undefined;
  tasksForIO: IdName[];
  currentTask: ITask | undefined;
}
const TASK_STATE_TOKEN = new StateToken<TasksStateModel>('tasks');
@State({
  name: TASK_STATE_TOKEN,
  defaults: {
    tasks: [],
    tasksOverview: [],
    tasksOverviewTotalPages: 0,
    taskList: [],
    taskLog: { records: [] },
    taskVersions: [],
    draftPerform: undefined,
    tasksForIO: [],
    currentTask: undefined,
  },
})
@Injectable()
export class TaskState {
  constructor(
    private service: TaskApiService,
    private store: Store
  ) {}

  @Action(LoadTasks, { cancelUncompleted: true })
  loadTasks({ getState, setState }: StateContext<TasksStateModel>, { filter }: LoadTasks) {
    return this.service.loadTasks(filter).pipe(
      tap((response: IApiResponseGroupTasks) => {
        const state = getState();
        setState({
          ...state,
          tasksOverview: response.groupData,
          tasksOverviewTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadTaskById, { cancelUncompleted: true })
  loadTaskById({ getState, setState }: StateContext<TasksStateModel>, { taskId }: LoadTaskById) {
    const { tasks } = getState();
    const taskExists = tasks.some((task) => task.id === taskId);
    return this.service.loadTaskById(taskId).pipe(
      tap((task) => {
        setState(
          patch({
            tasks: taskExists
              ? updateItem<ITask>((taskToUpdate) => taskToUpdate?.id === taskId, task)
              : insertItem<ITask>(task),
            currentTask: task,
          })
        );
      })
    );
  }
  @Action(LoadTaskVersion)
  loadTaskVersionById(
    { getState, setState }: StateContext<TasksStateModel>,
    { taskId, taskVersionId }: LoadTaskVersion
  ) {
    const { taskVersions } = getState();
    const taskExists = taskVersions.some((taskVersion) => taskVersion.id === taskVersionId);
    return this.service.loadTaskVersionById(taskId, taskVersionId).pipe(
      map((taskVersion) => {
        return { ...taskVersion, id: taskVersionId };
      }),
      tap((taskVersion) => {
        setState(
          patch({
            taskVersions: taskExists
              ? updateItem<ITaskPerformResponse>(
                  (taskVersionToUpdate) => taskVersionToUpdate?.id === taskVersionId,
                  taskVersion
                )
              : insertItem<ITaskPerformResponse>(taskVersion),
          })
        );
      })
    );
  }

  @Action(LoadTaskLog, { cancelUncompleted: true })
  loadTaskLogs({ getState, setState }: StateContext<TasksStateModel>, { taskId }: LoadTaskLog) {
    return this.service.loadTaskLog(taskId).pipe(
      tap((taskLog) => {
        const state = getState();
        setState({ ...state, taskLog });
      })
    );
  }

  @Action(LoadTasksForIO, { cancelUncompleted: true })
  loadTasksForIO({ getState, setState }: StateContext<TasksStateModel>) {
    return this.service.loadTasksForIO().pipe(
      tap((tasksForIO) => {
        const state = getState();
        setState({ ...state, tasksForIO });
      })
    );
  }

  @Action(CreateTask, { cancelUncompleted: true })
  @OnSuccess({ message: 'taskCreated' })
  createTask(ctx: StateContext<TasksStateModel>, { task }: CreateTask) {
    return this.service.createTask(task).pipe(
      tap(() => {
        const menuId =
          this.store.selectSnapshot(MenuTreeStructureState.getSelectedMenuId) ?? task.menuId;
        redirectAndBypassUnsavedDataCheck(`${UserTasksRoutes.Overview}/${menuId}`);
      })
    );
  }

  @Action(UpdateTask, { cancelUncompleted: true })
  @OnSuccess({ message: 'taskUpdated' })
  updateTask(ctx: StateContext<TasksStateModel>, { task }: UpdateTask) {
    return this.service.updateTask(task).pipe(
      tap(() => {
        const menuId =
          this.store.selectSnapshot(MenuTreeStructureState.getSelectedMenuId) ?? task.menuId;
        redirectAndBypassUnsavedDataCheck(`${UserTasksRoutes.Overview}/${menuId}`);
      })
    );
  }

  @Action(DeleteTask, { cancelUncompleted: true })
  @OnSuccess({ message: 'taskDeleted', dispatch: ResetCurrentFilter })
  deleteTask(ctx: StateContext<TasksStateModel>, { taskId }: DeleteTask) {
    return this.service.deleteTask(taskId).pipe(
      tap(() => {
        const menuId = this.store.selectSnapshot(MenuTreeStructureState.getSelectedMenuId);
        let url = `${UserTasksRoutes.Main}`;
        if (menuId) {
          url = `${UserTasksRoutes.Overview}/${menuId}`;
        }
        redirectAndBypassUnsavedDataCheck(url);
      })
    );
  }

  @Action(PostponeTask, { cancelUncompleted: true })
  @OnSuccess({ message: 'taskPostponed' })
  postponeTask(stateCtx: StateContext<TasksStateModel>, { taskPostpone }: PostponeTask) {
    return this.service.postponeTask(taskPostpone);
  }

  @Action(DeactivateTask, { cancelUncompleted: true })
  @OnSuccess({ message: 'taskDeactivated' })
  deactivateTask(stateCtx: StateContext<TasksStateModel>, { id }: DeactivateTask) {
    return this.service.deactivateTask(id).pipe(
      tap(() => {
        const menuId = this.store.selectSnapshot(MenuTreeStructureState.getSelectedMenuId);
        let url = `${UserTasksRoutes.Main}`;
        if (menuId) {
          url = `${UserTasksRoutes.Overview}/${menuId}`;
        }
        redirectAndBypassUnsavedDataCheck(url);
      })
    );
  }

  @Action(ReactivateTask, { cancelUncompleted: true })
  @OnSuccess({ message: 'taskReactivated' })
  reactivateTask(stateCtx: StateContext<TasksStateModel>, { id }: ReactivateTask) {
    return this.service.reactivateTask(id).pipe(
      tap(() => {
        const menuId = this.store.selectSnapshot(MenuTreeStructureState.getSelectedMenuId);
        let url = `${UserTasksRoutes.Main}`;
        if (menuId) {
          url = `${UserTasksRoutes.Overview}/${menuId}`;
        }
        redirectAndBypassUnsavedDataCheck(url);
      })
    );
  }

  @Action(CompleteTask, { cancelUncompleted: true })
  @OnSuccess({ message: 'taskCompleted' })
  completeTask(stateCtx: StateContext<TasksStateModel>, { id }: CompleteTask) {
    return this.service.completeTask(id).pipe(
      tap(() => {
        const menuId = this.store.selectSnapshot(MenuTreeStructureState.getSelectedMenuId);
        let url = `${UserTasksRoutes.Main}`;
        if (menuId) {
          url = `${UserTasksRoutes.Overview}/${menuId}`;
        }
        redirectAndBypassUnsavedDataCheck(url);
      })
    );
  }

  @Action(PerformTask, { cancelUncompleted: true })
  @OnSuccess({ message: 'taskPerformed' })
  performTask(stateCtx: StateContext<TasksStateModel>, { task }: PerformTask) {
    return this.service.performTask(task).pipe(
      tap(() => {
        const isFromDueList =
          this.store.selectSnapshot(DueListState.getLastDueListItem)?.id === task.id;
        if (!isFromDueList) {
          redirectAndBypassUnsavedDataCheck(`${UserTasksRoutes.Edit}/${task.id}`);
        } else {
          this.store.dispatch(new ClearLastDueListItem());
          redirectAndBypassUnsavedDataCheck(DueListRoutes.Main);
        }
      })
    );
  }

  @Action(RestoreTask, { cancelUncompleted: true })
  @OnSuccess({ message: 'taskRestored', url: UserTasksRoutes.Main })
  restoreTask(stateCtx: StateContext<TasksStateModel>, { id }: RestoreTask) {
    return this.service.restoreTask(id);
  }

  @Action(LoadTaskDraftPerform, { cancelUncompleted: true })
  loadTaskDraftPerform(
    { getState, setState }: StateContext<TasksStateModel>,
    { taskId }: LoadTaskDraftPerform
  ) {
    return this.service.loadTaskDraftPerform(taskId).pipe(
      tap((draftPerform) => {
        setState({ ...getState(), draftPerform });
      })
    );
  }

  @Action(SaveTaskDraftPerform, { cancelUncompleted: true })
  @OnSuccess({ message: 'draftSaved' })
  saveTaskDraftPerform(
    { getState, setState }: StateContext<TasksStateModel>,
    { task }: SaveTaskDraftPerform
  ) {
    return this.service.saveTaskDraftPerform(task).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(`${UserTasksRoutes.Edit}/${task.id}`);
      })
    );
  }

  @Action(ClearTasksByMenu, { cancelUncompleted: true })
  clearTasksByMenu(ctx: StateContext<TasksStateModel>) {
    ctx.setState({
      ...ctx.getState(),
      tasksOverview: [],
      tasksOverviewTotalPages: 0,
    });
  }

  @Action(ClearCurrentTask)
  clearCurrentTask(ctx: StateContext<TasksStateModel>) {
    ctx.setState(patch({ currentTask: undefined }));
  }

  @Selector()
  static getTasksOverview(state: TasksStateModel): ITasksGroupData[] {
    return state.tasksOverview;
  }
  @Selector()
  static getTasksOverviewTotalPages(state: TasksStateModel): number {
    return state.tasksOverviewTotalPages;
  }

  @Selector()
  static getCurrentTask(state: TasksStateModel): ITask | undefined {
    return state.currentTask;
  }

  static getTaskById(id: string): (state: TasksStateModel) => ITask | undefined {
    return createSelector([TaskState], (state: TasksStateModel) => {
      return state.tasks.find((task) => task.id === id);
    });
  }

  static getTaskPerformDataById(
    id: string
  ): (state: TasksStateModel) => ITaskPerformResponse | undefined {
    return createSelector([TaskState], (state: TasksStateModel) => {
      return state.taskVersions.find((version) => version.id === id);
    });
  }

  @Selector()
  static getTaskLog(state: TasksStateModel) {
    return state.taskLog;
  }

  @Selector()
  static getDraftPerform(state: TasksStateModel) {
    return state.draftPerform;
  }

  @Selector()
  static getTasksForIO(state: TasksStateModel) {
    return state.tasksForIO;
  }
}
